import { createColumnHelper } from '@tanstack/react-table'
import { CountryWidget } from 'entities/User/UserCard/blocks/CountryWidget'
import { IoPerson } from 'react-icons/io5'
import {
  TYPE_ENABLED_COLOR,
  TYPE_USER_COUNTRY,
  TYPE_USER_PLATFORM,
  getTypeStatusTitle,
  userIdentificationColor,
  userIdentificationLabel,
} from 'shared/constants/user'
import { getDateReadFormat, getDateTimeReadFormat } from 'shared/utils/dateFormatting'
import { EmptyCell } from 'uikit/ReactTable/cells/EmptyCell'
import { ItemBadge, ItemIcon, ItemLink } from 'uikit/Snippets'
import { PersonFullName } from 'uikit/Snippets/elements/PersonFullName'

type User = {
  id: string
  firstName: string
  lastName: string
  middleName: string
  phone: string
  identityCountry: keyof typeof TYPE_USER_COUNTRY
  identification: string
  isIDXApproved: boolean
  enabled: boolean
  trusted: boolean
  createDate: string
  identificationDate: string
  clientId: string
  passport: string
  contractNumber: string
  accountNumber: string
  dateIssue: string
  applicationVersion: string
  platform: keyof typeof TYPE_USER_PLATFORM
}

const columnHelper = createColumnHelper<User>()

export const columns = [
  columnHelper.accessor('id', {
    header: '',
    cell: () => <ItemIcon pl="10px" IconComponent={IoPerson} badge={null} />,
    maxSize: 80,
    minSize: 60,
    enableHiding: false,
  }),
  columnHelper.accessor(
    (row) => ({ firstName: row.firstName, lastName: row.lastName, middleName: row.middleName }),
    {
      header: 'ФИО',
      id: 'fullName',
      minSize: 240,
      maxSize: 300,
      cell: (info) => (
        <ItemLink>
          <PersonFullName
            firstName={info.getValue().firstName}
            lastName={info.getValue().lastName}
            middleName={info.getValue().middleName}
          />
        </ItemLink>
      ),
    }
  ),
  columnHelper.accessor('phone', {
    header: 'Телефон',
    minSize: 200,
    maxSize: 250,
    cell: (info) => info.getValue() || <EmptyCell />,
  }),
  columnHelper.accessor('identityCountry', {
    header: 'Страна',
    minSize: 150,
    maxSize: 200,
    cell: (info) => (
      <CountryWidget lang={info.getValue()}>{TYPE_USER_COUNTRY[info.getValue()]}</CountryWidget>
    ),
  }),
  columnHelper.accessor('identification', {
    header: 'Идентификация',
    minSize: 200,
    maxSize: 250,
    cell: (info) => (
      <ItemBadge colorPalette={userIdentificationColor[info.getValue()] || 'gray'}>
        {userIdentificationLabel[info.getValue()] || info.getValue()}
      </ItemBadge>
    ),
  }),
  columnHelper.accessor('isIDXApproved', {
    header: 'Идентификация по IDX',
    minSize: 100,
    maxSize: 250,
    cell: (info) => (info.getValue() ? 'Да' : 'Нет'),
  }),
  columnHelper.accessor('enabled', {
    header: 'Статус',
    minSize: 150,
    maxSize: 250,
    cell: (info) => (
      <ItemBadge colorPalette={TYPE_ENABLED_COLOR[info.getValue() ? 'true' : 'false']}>
        {getTypeStatusTitle(info.row.original)}
      </ItemBadge>
    ),
  }),
  columnHelper.accessor('trusted', {
    header: 'Снят лимит',
    minSize: 100,
    maxSize: 250,
    cell: (info) => (info.getValue() ? 'Да' : 'Нет'),
  }),
  columnHelper.accessor('createDate', {
    header: 'Дата регистрации',
    minSize: 150,
    maxSize: 250,
    cell: (info) => getDateTimeReadFormat(info.getValue()) || <EmptyCell />,
    enableSorting: true,
  }),
  columnHelper.accessor('identificationDate', {
    header: 'Дата идентификации',
    minSize: 150,
    maxSize: 250,
    cell: (info) => getDateTimeReadFormat(info.getValue()) || <EmptyCell />,
    enableSorting: true,
  }),
  columnHelper.accessor('clientId', {
    header: 'Client ID',
    minSize: 110,
    maxSize: 250,
    cell: (info) => info.getValue() || <EmptyCell />,
  }),
  columnHelper.accessor('passport', {
    header: 'Номер паспорта',
    minSize: 100,
    maxSize: 250,
    cell: (info) => info.getValue() || <EmptyCell />,
  }),
  columnHelper.accessor('contractNumber', {
    header: 'Номер договора',
    minSize: 110,
    maxSize: 250,
    cell: (info) => info.getValue() || <EmptyCell />,
  }),
  columnHelper.accessor('accountNumber', {
    header: 'Номер счета',
    minSize: 190,
    maxSize: 250,
    cell: (info) => info.getValue() || <EmptyCell />,
  }),
  columnHelper.accessor('dateIssue', {
    header: 'Дата выдачи',
    minSize: 100,
    maxSize: 250,
    cell: (info) => getDateReadFormat(info.getValue()) || <EmptyCell />,
  }),
  columnHelper.accessor('applicationVersion', {
    header: 'Версия',
    minSize: 60,
    maxSize: 100,
    cell: (info) => info.getValue() || <EmptyCell />,
  }),
  columnHelper.accessor('platform', {
    header: 'Платформа',
    minSize: 80,
    maxSize: 100,
    cell: (info) => TYPE_USER_PLATFORM[info.getValue()],
  }),
]
